import { useRouter } from 'next/router';
import { FC, memo } from 'react';
import React from 'react';
import { useRecoilState } from 'recoil';

import { Divider, Flex } from '@chakra-ui/react';

import { matomoConfig } from '@asa/asasqt-microfrontend-components/dist/common/matomo/config';
import { MatomoProvider } from '@asa/asasqt-microfrontend-components/dist/common/matomo/matomo.context';
import { isAuthenticatedAtom } from '@asa/asasqt-microfrontend-components/dist/common/state/shared';
import { View } from '@asa/asasqt-microfrontend-components/dist/layout/ui/View';

import { AuthProvider } from '@/core/auth/core/auth.context';
import { hasLicenseAtom } from '@/core/auth/core/auth.recoil';
import { AxiosContextProvider } from '@/core/common/axios/axios.context';
import { LanguageContextProvider } from '@/core/common/language';
import { AccountMenu, MainMenu } from '@/core/common/layout';
import { AccountMenuMobile } from '@/core/common/layout/AccountMenuMobile';
import { hasLegalDocsToAcceptAtom } from '@/core/common/recoil';
import msYaheiFontPath from '@/theme/fonts/MSYahei.ttf';
import notoThaiFontPath from '@/theme/fonts/NotoSansThai-Regular.ttf';
import robotoBoldFontPath from '@/theme/fonts/Roboto-Bold.ttf';
import robotoItalicFontPath from '@/theme/fonts/Roboto-Italic.ttf';
import robotoFontPath from '@/theme/fonts/Roboto-Regular.ttf';

export const Providers: FC<React.PropsWithChildren<unknown>> = memo(
  ({ children }) => {
    const [isAuthenticated] = useRecoilState<boolean>(isAuthenticatedAtom);
    const [hasLicense] = useRecoilState(hasLicenseAtom);
    const [hasLegalDocsToAccept] = useRecoilState(hasLegalDocsToAcceptAtom);
    const router = useRouter();
    const { siteIds, excludedQueryParams } = matomoConfig.sqt;

    if (!router.isReady) {
      return null;
    }

    const fontFacesStyle = `
    @font-face {
      font-family: 'Roboto';
      src: url('${robotoFontPath}') format('truetype');
      font-weight: 400;
      font-style: normal;
    }
    @font-face {
      font-family: 'Roboto';
      src: url('${robotoBoldFontPath}') format('truetype');
      font-weight: 700;
      font-style: normal;
    }
    @font-face {
      font-family: 'Roboto';
      src: url('${robotoItalicFontPath}') format('truetype');
      font-weight: 400;
      font-style: italic;
    }
    @font-face {
      font-family: 'Noto';
      src: url('${notoThaiFontPath}') format('truetype');
      font-weight: 400;
      font-style: normal;
    }
    @font-face {
      font-family: 'MSYahei';
      src: url('${msYaheiFontPath}') format('truetype');
      font-weight: 400;
      font-style: normal;
    }
  `;

    return (
      <React.Fragment>
        <style jsx global>
          {fontFacesStyle}
        </style>
        <AxiosContextProvider>
          <LanguageContextProvider>
            <MatomoProvider
              siteIds={siteIds}
              excludedQueryParams={excludedQueryParams}
            >
              <AuthProvider>
                {hasLicense ? (
                  <View
                    isAuthenticated={isAuthenticated}
                    hasLegalDocsToAccept={hasLegalDocsToAccept}
                    topBarComponents={
                      <React.Fragment>
                        <MainMenu
                          m="4"
                          display={{ base: 'none', md: 'flex' }}
                        />
                        <AccountMenu />
                      </React.Fragment>
                    }
                    topBarComponentsMobile={
                      <Flex flexDirection="column">
                        <MainMenu isDrawer />
                        <Divider my={3} borderColor={'gray.400'} />
                        <AccountMenuMobile />
                      </Flex>
                    }
                  >
                    {children}
                  </View>
                ) : (
                  children
                )}
              </AuthProvider>
            </MatomoProvider>
          </LanguageContextProvider>
        </AxiosContextProvider>
      </React.Fragment>
    );
  }
);
